import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Paper } from '@material-ui/core';

import { createOrUpdateJobOverride } from '../../../../../applicant-automations/actions/create-or-update-job-override';
import { loadApplicantAutomations } from '../../../../../applicant-automations/actions/load-applicant-automations';
import { applicantAutomationsForOrgIdSelector } from '../../../../../applicant-automations/applicant-automation-selectors';
import { userSelector } from '../../../../../reducers/current-user-selectors';

import { ApplicantMessageTemplateParser } from '../../../../../message-templates/parsers/applicant-message-template-parser';
import { UiButton } from '../../../../../ui/ui-button';
import { UiDialog } from '../../../../../ui/ui-dialog';
import { UiGreenSwitch } from '../../../../../ui/ui-green-switch';
import { UiHideShowToggle } from '../../../../../ui/ui-hide-show-toggle';

import { ApplicantAutomationDialog } from '../../../../../automations/components/widgets/applicant-automation-dialog';
import { ApplicantAutomationMessage } from '../../../../../automations/components/widgets/applicant-automation-message';
import { EditAutomationDialog } from '../../../../../automations/components/widgets/edit-automation-dialog';
import {
  APPLICANT_AUTOMATION_COMMUNICATION_TYPES,
  CREATABLE_APPLICANT_AUTOMATION_TYPES,
} from '../../../../../automations/utils/applicant-automation-communication-types';
import {
  getApplicantAutomationCreateDefaults,
  getApplicationAutomationTiming,
  getAutomationsForContext,
  getSectionForApplicantAutomation,
  getTitleWithFallback,
  sortAutomations,
} from '../../../../../automations/utils/applicant-automation-display-utils';

export const ResponsiveTransition = ({
  job,
  org,
  onCancel,
  onConfirmation,
}: {
  job: Job;
  org: Org;
  onCancel: () => void;
  onConfirmation: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  const { org_id: orgId, name: orgName } = org;
  const { job_id: jobId } = job;

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadApplicantAutomations({ orgId, jobId }));
  });

  const user = useSelector((state: any) => userSelector(state));
  const applicantAutomations =
    useSelector((state: any) => applicantAutomationsForOrgIdSelector(state, orgId)) || [];
  const interestConfirmations = getAutomationsForContext(applicantAutomations, orgId, jobId).filter(
    (automation) =>
      automation.communicationType ===
      APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION,
  );
  const sortedInterestConfirmations = sortAutomations(interestConfirmations);

  const hasAtLeastOneActiveInterestConfirmation =
    interestConfirmations.filter((automation) => automation.isActive).length > 0;

  const [showDetailsForId, setShowDetailsForId] = useState<number | null>(null);
  const [creatingAutomation, setCreatingAutomation] = useState<boolean>(false);
  const [editingAutomation, setEditingAutomation] = useState<ApplicantAutomation | null>(null);

  const toggleShowDetails = (automationId: number) => () => {
    if (automationId === showDetailsForId) {
      setShowDetailsForId(null);
    } else {
      setShowDetailsForId(automationId);
    }
  };

  const toggleAutomationOnOff = (automationToUpdate: ApplicantAutomation) => () => {
    dispatch(
      createOrUpdateJobOverride(automationToUpdate, {
        jobId,
        isActive: !automationToUpdate.isActive,
      }),
    );
  };

  const toggleCreateDialog = () => {
    setCreatingAutomation(!creatingAutomation);
  };

  const toggleEditDialog = (automationToEdit: ApplicantAutomation | null) => () => {
    setEditingAutomation(automationToEdit);
  };

  const parser = new ApplicantMessageTemplateParser({
    useStandInApplicant: true,
    job,
    user,
  });

  return (
    <UiDialog size="sm" title="Automatic Outreach" onClose={onCancel}>
      <UiDialog.Content>
        <div>
          To make sure an applicant will be responsive, we send an automatic interest confirmation
          text and ask for a response.
          {!sortedInterestConfirmations.length
            ? ' Please create an interest confirmation below '
            : ' Feel free to edit the messages we send below '}
          before confirming. You can also make changes at anytime in the Automations tab.
        </div>
        {sortedInterestConfirmations.map((interestConfirmation) => (
          <div
            key={`responsive-transition-applicant-automation-${interestConfirmation.applicantTriggeredCommunicationId}`}
            className="u-margin-y-md"
          >
            <Paper className="u-margin-y u-padding u-width-100">
              <div className="u-display-flex u-justifyContent-spaceBetween">
                <div className="typo-title">{getTitleWithFallback(interestConfirmation)}</div>
                <UiGreenSwitch
                  checked={!!interestConfirmation.isActive}
                  onChange={toggleAutomationOnOff(interestConfirmation)}
                />
              </div>
              <UiHideShowToggle
                isOpen={showDetailsForId === interestConfirmation.applicantTriggeredCommunicationId}
                onChange={toggleShowDetails(interestConfirmation.applicantTriggeredCommunicationId)}
                title="Details"
              />
              {showDetailsForId === interestConfirmation.applicantTriggeredCommunicationId ? (
                <div className="u-margin-top u-display-flex u-flexDirection-column">
                  <div className="typo-subtitle">
                    {getApplicationAutomationTiming(interestConfirmation)}
                  </div>
                  <ApplicantAutomationMessage automation={interestConfirmation} orgName={orgName} />
                  <UiButton
                    className="u-alignSelf-flexEnd"
                    onClick={toggleEditDialog(interestConfirmation)}
                    variant="tertiary"
                  >
                    Edit
                  </UiButton>
                </div>
              ) : null}
            </Paper>
            {editingAutomation && (
              <EditAutomationDialog
                editingAutomation={editingAutomation}
                title={getTitleWithFallback(interestConfirmation)}
                org={org}
                onClose={toggleEditDialog(null)}
                isEditingRecipientsOnly={false}
                applicantAutomationSection={getSectionForApplicantAutomation(editingAutomation)}
                editingForJob={job}
              />
            )}
          </div>
        ))}
        {creatingAutomation && (
          <ApplicantAutomationDialog
            key="creating-automation-hqc-responsive-transition"
            parser={parser}
            section="application"
            orgId={org.org_id}
            orgName={org.name}
            addingAutomationForTrigger={
              APPLICANT_AUTOMATION_COMMUNICATION_TYPES.INTEREST_CONFIRMATION
            }
            editingForJob={job}
            onClose={toggleCreateDialog}
            automation={getApplicantAutomationCreateDefaults(
              CREATABLE_APPLICANT_AUTOMATION_TYPES.INTEREST_CONFIRMATION,
            )}
          />
        )}
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton variant="dismiss" onClick={onCancel}>
          Cancel
        </UiButton>
        {sortedInterestConfirmations.length ? (
          <UiButton onClick={onConfirmation} disabled={!hasAtLeastOneActiveInterestConfirmation}>
            Okay
          </UiButton>
        ) : (
          <UiButton onClick={toggleCreateDialog}>Create Automation</UiButton>
        )}
      </UiDialog.Actions>
    </UiDialog>
  );
};
